import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { Download, Loader2, Printer, X } from 'lucide-react';

import { EmailButton } from './EmailButton';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type PrintingActionButtonsProps = {
  selectedCount: number;
  onPrint: () => Promise<void>;
  onDownload: () => Promise<void>;
  onSendEmail: (emails: string[]) => Promise<void>;
  onPostPrint?: () => void;
  isPrinting?: boolean;
  isDownloading?: boolean;
  isEmailSending?: boolean;
  isDisabled?: boolean;
};

export const PrintingActionButtons: React.FC<PrintingActionButtonsProps> = ({
  selectedCount,
  onPrint,
  onDownload,
  onSendEmail,
  onPostPrint,
  isPrinting = false,
  isDownloading = false,
  isEmailSending = false,
  isDisabled = false,
}) => {
  const t = useTranslations();

  const handlePrint = async () => {
    try {
      await onPrint();
      onPostPrint?.();
    } finally {
      // No need to close anything here since it's not in a floating bar
    }
  };

  const handleDownload = async () => {
    try {
      await onDownload();
      onPostPrint?.();
    } finally {
      // No need to close anything here since it's not in a floating bar
    }
  };

  return (
    <div className="@PrintingActionButtons contents">
      <Button
        variant="outline"
        size="default"
        icon={
          isDownloading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )
        }
        onPress={handleDownload}
        isDisabled={
          selectedCount === 0 ||
          isDownloading ||
          isPrinting ||
          isEmailSending ||
          isDisabled
        }
        className="w-[120px]"
      >
        {isDownloading
          ? t.bookingDetail.documents.actions.downloading
          : t.bookingDetail.documents.actions.download}
      </Button>
      <EmailButton
        onSend={onSendEmail}
        isDisabled={
          selectedCount === 0 ||
          isEmailSending ||
          isPrinting ||
          isDownloading ||
          isDisabled
        }
        isLoading={isEmailSending}
      />
      <Button
        variant="outline"
        size="default"
        icon={
          isPrinting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Printer size={14} />
          )
        }
        onPress={handlePrint}
        isDisabled={
          selectedCount === 0 ||
          isPrinting ||
          isEmailSending ||
          isDownloading ||
          isDisabled
        }
        className="w-[180px]"
      >
        {isPrinting
          ? t.bookingDetail.documents.actions.printing
          : t.bookingDetail.documents.actions.printWithBrowser}
      </Button>
    </div>
  );
};

type Props = {
  selectedCount: number;
  isOpen: boolean;
  onClose: () => void;

  onPrint: () => Promise<void>;
  onDownload: () => Promise<void>;
  onSendEmail: (emails: string[]) => Promise<void>;
  onPostPrint?: () => void;
  isPrinting?: boolean;
  isDownloading?: boolean;
  isEmailSending?: boolean;
};

export const FloatingActionBar: React.FC<Props> = ({
  selectedCount,
  isOpen,
  onClose,
  onPrint,
  onDownload,
  onSendEmail,
  onPostPrint,
  isPrinting = false,
  isDownloading = false,
  isEmailSending = false,
}) => {
  const t = useTranslations();

  const handleClose = () => {
    onClose();
  };

  return (
    <div
      className={cn(
        '@FloatingActionBar',
        'absolute bottom-0 left-1/2 -translate-x-1/2 transform transition-all duration-200',
        isOpen ? 'mb-6 translate-y-0 opacity-100' : 'translate-y-10 opacity-0',
      )}
    >
      <div className="flex min-w-[726px] items-center justify-between gap-[20px] rounded-[8px] border border-neutral/10 bg-white px-3 py-2 shadow-lg">
        <div className="flex flex-row items-center gap-2">
          <div className="flex h-[26px] min-w-[26px] items-center justify-center rounded-md bg-primary text-base font-semibold text-white">
            {selectedCount}
          </div>
          <div className="text-sm font-medium text-neutral/80">
            {t.bookingDetail.documents.labels.selected}
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <PrintingActionButtons
            selectedCount={selectedCount}
            onPrint={onPrint}
            onDownload={onDownload}
            onSendEmail={onSendEmail}
            onPostPrint={onPostPrint}
            isPrinting={isPrinting}
            isDownloading={isDownloading}
            isEmailSending={isEmailSending}
          />
        </div>
        <Button
          variant="text"
          size="icon"
          icon={<X />}
          onPress={handleClose}
          isDisabled={isPrinting || isEmailSending || isDownloading}
        />
      </div>
    </div>
  );
};
